// API Service for FilmIQ Widget Dashboard
import {
  SceneAnalysisData,
  ApiResponse,
  DelayAnalysisData,
  TimingTowerData,
  ProductionAssumptionsData,
  DocumentTrackingData,
} from '../types/widget';
import { auth } from '../firebase';
import { mockAssumptionsData } from './mockAssumptionsData';
import { getDemoMode } from '../utils/demoMode';
import { 
  demoDay0DriftAnalysisData, 
  demoDay0ProductionAssumptionsData, 
  demoDay0TimingTowerData,
} from './demoDay0Data';
import {
  demoDay10TimingTowerData,
  demoDay10DriftAnalysisData,
} from './demoDay10Data';

import { apiConfig } from '../config';

// API URL configuration - from centralized config
const API_BASE_URL = apiConfig.baseUrl;

// Request timeout from config
const REQUEST_TIMEOUT = apiConfig.timeout;

class ApiService {
  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<ApiResponse<T>> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT);

    try {
      const userEmail = auth.currentUser?.email || '';
      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        headers: {
          'Content-Type': 'application/json',
          'x-user-email': userEmail,
          ...options.headers,
        },
        signal: controller.signal,
        ...options,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        // Provide user-friendly error messages
        let errorMessage = 'Unable to load data';
        if (response.status === 404) {
          errorMessage = 'Data not found';
        } else if (response.status >= 500) {
          errorMessage = 'Server error. Please try again later.';
        } else if (response.status === 0) {
          errorMessage = 'Unable to connect to server. Please check your connection.';
        }
        
        throw new Error(errorMessage);
      }

      const serverResponse = await response.json();
      return {
        success: serverResponse.success !== false, // Default to true if not specified
        data: serverResponse.data,
        timestamp: serverResponse.timestamp || new Date().toISOString(),
      };
    } catch (error) {
      clearTimeout(timeoutId);
      
      // Handle timeout and network errors
      let errorMessage = 'Unable to load data';
      if (error instanceof Error) {
        if (error.name === 'AbortError') {
          errorMessage = 'Request timed out. Please try again.';
        } else if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
          errorMessage = 'Unable to connect to server. Please check your connection and try again.';
        } else {
          errorMessage = error.message;
        }
      }
      
      console.error(`API Error [${endpoint}]:`, error);
      
      return {
        success: false,
        error: errorMessage,
        timestamp: new Date().toISOString(),
      };
    }
  }

  // Scene Analysis API
  async getSceneAnalysisData(day?: number): Promise<ApiResponse<SceneAnalysisData>> {
    // Check for demo mode
    const demoMode = getDemoMode();
    
    // demo=day0: Show original schedule (12 days, nothing shot)
    // For day0, we return a simplified version showing planned scenes only
    if (demoMode === 'day0') {
      // For day0 demo, show original 12-day schedule
      // Days 1-12 exist, Day 13+ does not exist
      const requestedDay = day || 1;
      if (requestedDay > 12) {
        return {
          success: true,
          data: {
            day: requestedDay,
            date: 'N/A',
            scheduleVersion: 'Original (White)',
            plannedScenes: [],
            actualScenes: [],
            analysis: {
              totalPlannedPages: 0,
              totalShotPages: 0,
              dailyVariance: 0,
              cumulativeDrift: 0,
              scenesNotShot: [],
              scenesShotNotPlanned: [],
              reasons: [],
              status: 'on_track',
              summary: 'This day does not exist in the original 12-day schedule.',
            },
          } as SceneAnalysisData,
          timestamp: new Date().toISOString(),
        };
      }
      
      // Get day0 scene data from demoDay0TimingTowerData
      const dayData = demoDay0TimingTowerData.days.find(d => d.dayNumber === requestedDay);
      if (dayData) {
        return {
          success: true,
          data: {
            day: requestedDay,
            date: 'Pre-Production',
            scheduleVersion: 'Original (White)',
            plannedScenes: Array.isArray(dayData.scenesPlanned) 
              ? dayData.scenesPlanned.map(s => ({
                  sceneNumber: s,
                  description: '',
                  pages: 0,
                  dayNightDesignation: '',
                  priorityLevel: 1,
                  location: '',
                })) 
              : [],
            actualScenes: [], // Nothing shot in day0 demo
            analysis: {
              totalPlannedPages: dayData.plannedPagesNumeric || 0,
              totalShotPages: 0,
              dailyVariance: -(dayData.plannedPagesNumeric || 0),
              cumulativeDrift: 0,
              scenesNotShot: Array.isArray(dayData.scenesPlanned) ? dayData.scenesPlanned : [],
              scenesShotNotPlanned: [],
              reasons: [],
              status: 'on_track',
              summary: `Original schedule: ${dayData.plannedScenesCount || 0} scenes planned for Day ${requestedDay}. Production has not started.`,
            },
          } as SceneAnalysisData,
          timestamp: new Date().toISOString(),
        };
      }
    }
    
    // demo=day10: Days 1-10 have actual data, Days 11-13 are planned only
    if (demoMode === 'day10') {
      const requestedDay = day || 1;
      
      // Days 11-13 are planned but not yet shot in Day 10 demo
      if (requestedDay >= 11 && requestedDay <= 13) {
        const dayData = demoDay10TimingTowerData.days.find(d => d.dayNumber === requestedDay);
        if (dayData) {
          return {
            success: true,
            data: {
              day: requestedDay,
              date: 'Scheduled',
              scheduleVersion: 'Goldenrod (4/11/2016)',
              plannedScenes: Array.isArray(dayData.scenesPlanned)
                ? dayData.scenesPlanned.map(s => ({
                    sceneNumber: s,
                    description: '',
                    pages: 0,
                    dayNightDesignation: '',
                    priorityLevel: 1,
                    location: '',
                  }))
                : [],
              actualScenes: [], // Not shot yet in Day 10 demo
              analysis: {
                totalPlannedPages: dayData.plannedPagesNumeric || 0,
                totalShotPages: 0,
                dailyVariance: 0,
                cumulativeDrift: 0,
                scenesNotShot: Array.isArray(dayData.scenesPlanned) ? dayData.scenesPlanned : [],
                scenesShotNotPlanned: [],
                reasons: [],
                status: 'on_track',
                summary: `Day ${requestedDay} is scheduled but not yet shot (as of Day 10).`,
              },
            } as SceneAnalysisData,
            timestamp: new Date().toISOString(),
          };
        }
      }
      
      // Days 1-10: Use production API for actual shot data
      const endpoint = day ? `/scene-analysis/${day}` : '/scene-analysis';
      return this.request<SceneAnalysisData>(endpoint);
    }
    
    // Production mode: Use production API
    const endpoint = day ? `/scene-analysis/${day}` : '/scene-analysis';
    return this.request<SceneAnalysisData>(endpoint);
  }

  // Drift Analysis API
  async getDriftAnalysisData(): Promise<ApiResponse<any[]>> {
    // Check for demo mode
    const demoMode = getDemoMode();
    
    // demo=day0: No drift data (production hasn't started)
    if (demoMode === 'day0') {
      return {
        success: true,
        data: demoDay0DriftAnalysisData,
        timestamp: new Date().toISOString(),
      };
    }
    
    // demo=day10: Show drift data only for Days 1-10
    if (demoMode === 'day10') {
      return {
        success: true,
        data: demoDay10DriftAnalysisData,
        timestamp: new Date().toISOString(),
      };
    }
    
    // Production mode: Use production API
    return this.request<any[]>('/drift-analysis');
  }

  // Drift Trajectory API (for velocity chart)
  async getDriftTrajectoryData(): Promise<ApiResponse<any>> {
    return this.request<any>('/drift-trajectory');
  }

  async refreshDriftTrajectory(): Promise<ApiResponse<any>> {
    return this.request<any>('/drift-trajectory/refresh', { method: 'POST' });
  }

  // Production Data API
  async getProductionData(): Promise<ApiResponse<any>> {
    return this.request<any>('/production');
  }

  // Delay Analysis API
  async getDelayAnalysisData(): Promise<ApiResponse<DelayAnalysisData>> {
    return this.request<DelayAnalysisData>('/delay-analysis');
  }

  // Production Daily Focus API
  async getProductionDailyFocusData(day: number): Promise<ApiResponse<any[]>> {
    return this.request<any[]>(`/production-daily-focus/${day}`);
  }

  // Shoot Days API (get all shoot days with dates)
  async getShootDays(): Promise<ApiResponse<any[]>> {
    return this.request<any[]>('/shoot-days');
  }

  // Production Info API (for max day, etc.)
  async getProductionInfo(): Promise<ApiResponse<any>> {
    return this.request<any>('/production-info');
  }

  // Timing Tower API
  async getTimingTowerData(): Promise<ApiResponse<TimingTowerData>> {
    // Check for demo mode
    const demoMode = getDemoMode();
    
    // demo=day0: Show original 12-day schedule (nothing shot)
    if (demoMode === 'day0') {
      return {
        success: true,
        data: demoDay0TimingTowerData,
        timestamp: new Date().toISOString(),
      };
    }
    
    // demo=day10: Show production state as of Day 10
    // Days 1-10 completed, Days 11-13 planned (not yet shot)
    if (demoMode === 'day10') {
      return {
        success: true,
        data: demoDay10TimingTowerData,
        timestamp: new Date().toISOString(),
      };
    }
    
    // Production mode: Use production API
    return this.request<TimingTowerData>('/timing-tower');
  }

  async getProductionAssumptionsData(): Promise<ApiResponse<ProductionAssumptionsData>> {
    // Check for demo mode - Day 0 shows all Current values as 0
    const demoMode = getDemoMode();
    if (demoMode === 'day0') {
      return {
        success: true,
        data: demoDay0ProductionAssumptionsData,
        timestamp: new Date().toISOString(),
      };
    }
    // Day 10 and production mode use same data (as per requirements)
    
    // Production mode - make real API call
    const response = await this.request<ProductionAssumptionsData>('/production-assumptions');

    if (response.success && response.data) {
      return response;
    }

    console.warn('Falling back to mock Production Assumptions data', response.error);

    return {
      success: true,
      data: mockAssumptionsData,
      error: response.error,
      timestamp: new Date().toISOString(),
    };
  }

  // Budget Summary API (for Budget Tracker widget)
  async getBudgetSummaryData(): Promise<ApiResponse<any>> {
    return this.request<any>('/budget-summary');
  }

  // Widget Configuration API
  async getWidgetConfig(widgetId: string): Promise<ApiResponse<any>> {
    return this.request<any>(`/widgets/${widgetId}`);
  }

  async saveWidgetConfig(widgetId: string, config: any): Promise<ApiResponse<any>> {
    return this.request<any>(`/widgets/${widgetId}`, {
      method: 'PUT',
      body: JSON.stringify(config),
    });
  }

  // Dashboard Configuration API
  async getDashboardConfig(dashboardId: string): Promise<ApiResponse<any>> {
    return this.request<any>(`/dashboards/${dashboardId}`);
  }

  async saveDashboardConfig(dashboardId: string, config: any): Promise<ApiResponse<any>> {
    return this.request<any>(`/dashboards/${dashboardId}`, {
      method: 'PUT',
      body: JSON.stringify(config),
    });
  }

  // Document Tracking API
  async getDocumentTrackingData(): Promise<ApiResponse<DocumentTrackingData>> {
    return this.request<DocumentTrackingData>('/document-tracking');
  }

  // Document Library API
  async getDocumentLibraryData(documentType?: string): Promise<ApiResponse<any>> {
    const params = documentType ? `?document_type=${documentType}` : '';
    return this.request<any>(`/documents/library${params}`);
  }

  // Document Signed URL for preview/download
  async getDocumentSignedUrl(gcsUri: string): Promise<ApiResponse<{
    signed_url: string;
    file_name: string;
    content_type: string;
    size_bytes: number;
    expires_in_minutes: number;
  }>> {
    return this.request<any>(`/document/signed-url?gcs_uri=${encodeURIComponent(gcsUri)}`);
  }

  // Production Audit Log API
  async getAuditLog(options: {
    limit?: number;
    offset?: number;
    category?: string;
    shootDay?: number;
  } = {}): Promise<ApiResponse<AuditLogResponse>> {
    const params = new URLSearchParams();
    if (options.limit) params.append('limit', options.limit.toString());
    if (options.offset) params.append('offset', options.offset.toString());
    if (options.category) params.append('category', options.category);
    if (options.shootDay !== undefined) params.append('shoot_day', options.shootDay.toString());
    
    const queryString = params.toString();
    return this.request<AuditLogResponse>(`/audit-log${queryString ? `?${queryString}` : ''}`);
  }
}

// Audit Log Types
export interface AuditLogEvent {
  id: number;
  eventType: string;
  category: string;
  categoryLabel: string;
  categoryIcon: string;
  categoryColor: string;
  shootDay: number | null;
  title: string;
  description: string | null;
  sourceDocument: string | null;
  referenceType: string | null;
  referenceId: number | null;
  metadata: Record<string, any>;
  timestamp: string;
  eventDate: string;
  eventTime: string;
}

export interface AuditLogResponse {
  events: AuditLogEvent[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
    hasMore: boolean;
  };
  categories: Record<string, {
    label: string;
    icon: string;
    color: string;
  }>;
}

export const apiService = new ApiService();
export default apiService;
